import ConvertToArray from './ConvertToArray';
import PadLeftWithLeadingZeros from './PadLeftWithLeadingZeros';
import ParseTimeString from './ParseTimeString';
import BlockedWarningMessages from './BlockedWarningMessages';
import ConvertTime12to24 from './ConvertTime12to24';
import OfficeHoursMonitoring from './OfficeHoursMonitoring';
import getParameterCaseInsensitive from './getParameterCaseInsensitive';
import IsUrlBlocked from './IsUrlBlocked';

/**
 * Company Level Validation
 * Block invitation and EP on pages
 * */
class CompanyLevelBlockValidator {
    constructor(companyLevelBlockSettings, companyContextRequest, companyLevelSettings, pageUrl) {
        this.options = {
            companyLevelBlockSettings,
            companyContextRequest,
            companyLevelSettings
        };
        this.pageUrl = pageUrl || window.location.href; // url of client's website
    }

    isBlockedInvitationOnPage() {
        let hideInvitation = false;
        if (
            this.options.companyLevelBlockSettings.allowInvitationOnPages &&
            !IsUrlBlocked(
                this.options.companyLevelBlockSettings.allowInvitationOnPages,
                this.pageUrl
            )
        ) {
            // If it does not match, return from here because we are only allowing invitation on certain pages.
            hideInvitation = true;
            console.warn(BlockedWarningMessages.Invitation);
        } else if (
            this.options.companyLevelBlockSettings.blockInvitationOnPages &&
            IsUrlBlocked(
                this.options.companyLevelBlockSettings.blockInvitationOnPages,
                this.pageUrl
            )
        ) {
            // if found, do not execute any further, return from here.
            hideInvitation = true;
            console.warn(BlockedWarningMessages.Invitation);
        }
        return hideInvitation;
    }

    isBlockedExitpopupOnPage() {
        let hideExitPopup = false;
        // show invitation on certain pages
        if (
            this.options.companyLevelBlockSettings.allowExitPopUpOnPages &&
            !IsUrlBlocked(
                this.options.companyLevelBlockSettings.allowExitPopUpOnPages,
                this.pageUrl
            )
        ) {
            // If it does not matches, return from here because we are only allowing invitation on certain pages.
            hideExitPopup = true;
            console.warn(BlockedWarningMessages.ExitPopup);
        } else if (
            this.options.companyLevelBlockSettings.blockExitPopUpOnPages &&
            IsUrlBlocked(this.options.companyLevelBlockSettings.blockExitPopUpOnPages, this.pageUrl)
        ) {
            // if found, do not execute any further, return from here.
            hideExitPopup = true;
            console.warn(BlockedWarningMessages.ExitPopup);
        }
        return hideExitPopup;
    }

    /**
     * Get cloudflare connecting IP
     * @param {any} req context request object
     */
    GetConnectingIP(req) {
        if (req && req.Headers) {
            let headers = req.Headers; // get headers from context object
            headers = JSON.parse(headers);
            const headerValue = getParameterCaseInsensitive(headers, 'cf-connecting-ip');
            return headerValue && headerValue[0];
        }
        return null;
    }

    /**
     * Get forwarded for IP
     * @param {any} req context request object
     */
    GetForwardedForIP(req) {
        if (req && req.Headers) {
            let headers = req.Headers; // get headers from context object
            if (req.HasRequest) {
                return null;
            }
            headers = JSON.parse(headers);
            const headerValue = getParameterCaseInsensitive(headers, 'x-forwarded-for');
            return headerValue && headerValue[0];
        }
        return null;
    }

    /**
     * Get visitor IP
     * @param {any} request context request object
     */
    GetVisitorIP(request) {
        let ip = this.GetConnectingIP(request); // first check if CloudFlare connecting IP exists
        if (!ip) {
            ip = this.GetForwardedForIP(request); // then check if Forwarded for IP exists
            if (!ip) {
                ip = this.options.companyContextRequest.UserHostAddress; // if both above doesnt exist assign user host address
            }
        }
        return ip;
    }

    /**
     * Check for IP blocked on page
     * */
    isBlockedIP() {
        let hideInvitation = false;
        const ip = this.GetVisitorIP(this.options.companyContextRequest); // get visitor IP
        if (
            this.options.companyLevelBlockSettings.blockedIPAddresses &&
            ConvertToArray(this.options.companyLevelBlockSettings.blockedIPAddresses).indexOf(
                ip
            ) !== -1
        ) {
            hideInvitation = true;
            console.warn(BlockedWarningMessages.IP);
        }
        return hideInvitation;
    }

    /**
     * Blocked invitation on basis of each blocking type.
     * */
    isBlockedInvitation() {
        let blockedInvitation = false;
        blockedInvitation = this.isBlockedInvitationOnPage(); // first check for invitation blocked on page.
        blockedInvitation = !blockedInvitation ? this.isBlockedIP() : blockedInvitation; // if invitation blocked not available then check for IP block
        blockedInvitation = !blockedInvitation ? this.isOfficeHoursBlocked() : blockedInvitation; // if invitation blocked not available then check for IP block
        return blockedInvitation;
    }

    /**
     * Blocked EP on basis of each blocking type.
     * */
    isBlockedExitpopup() {
        let blockedEP = false;
        blockedEP = this.isBlockedExitpopupOnPage(); // first check for EP blocked on page.
        blockedEP = !blockedEP ? this.isBlockedIP() : blockedEP; // if EP blocked not available then check for IP block
        blockedEP = !blockedEP ? this.isOfficeHoursBlocked() : blockedEP; // if EP blocked not available then check for Office Hours blocked block
        return blockedEP;
    }

    /**
     * Check for blocked SMS Number
     * @param {string} number
     */
    isSMSNumberBlocked(num) {
        let numberBlocked = false;
        if (num) {
            // show invitation on certain pages
            if (
                this.options.companyLevelBlockSettings.blockedSMSNumbers &&
                ConvertToArray(this.options.companyLevelBlockSettings.blockedSMSNumbers).indexOf(
                    num
                ) !== -1
            ) {
                numberBlocked = true;
                console.warn(BlockedWarningMessages.SMS + num);
            }
        }
        return numberBlocked;
    }

    /**
     * Check for company office hours
     * */
    isOfficeHoursBlocked() {
        let notAvailable = false;
        const noOfDaysInAWeek = 7;
        if (this.options.companyLevelSettings) {
            const { companyLevelSettings } = this.options;
            const now = new Date(); // current date time.
            const startTimeArray = ConvertToArray(companyLevelSettings.officeHoursStart); // array of start time from server
            const endTimeArray = ConvertToArray(companyLevelSettings.officeHoursEnd); // array of end time from server
            const baseUTCOffSet =
                (companyLevelSettings.officeHoursTimezone &&
                    companyLevelSettings.officeHoursTimezone.baseUtcOffset) ||
                0; // timezone offset from server.
            const timeZoneOffSet = parseInt(baseUTCOffSet);
            let hoursNow = 0;
            if (
                companyLevelSettings.officeHoursTimezone.SupportsDaylightSavingTime &&
                companyLevelSettings.isDayLightSavingTime
            ) {
                hoursNow = PadLeftWithLeadingZeros(now.getUTCHours() + (timeZoneOffSet + 1), 2); // get current UTC time and take difference from offset of timzone of server.
            } else {
                hoursNow = PadLeftWithLeadingZeros(now.getUTCHours() + timeZoneOffSet, 2); // get current UTC time and take difference from offset of timzone of server.
            }
            // CHAT-13019: for brisbane offset +10 the hoursNow at UTC 18 was becoming 28
            // must rotate the clock to 0 after 23
            // also day index will shift by 1 as the day
            // we must pick the utc dayindex rather then local
            let dayIndex = now.getUTCDay();
            if (hoursNow > 23) {
                hoursNow -= 24;
                dayIndex++;
            }

            // incase hours now less then zero, minus from 24 to get actual hour
            // reduce dayindex by 1 as day will be different then utc day
            else if (hoursNow < 0) {
                hoursNow = 24 + hoursNow;
                dayIndex--;
            }

            // need to fix day index if it gets 7 or -1
            // must rotate the day index
            // since we are now calculating the time by where the company exists
            if (dayIndex > 6) dayIndex = 0;
            if (dayIndex < 0) dayIndex = 6;

            const minsNow = PadLeftWithLeadingZeros(now.getMinutes(), 2); // get mins.
            const timeNow = ParseTimeString(`${hoursNow}:${minsNow}`); // time set according to server specified timezone.
            if (
                startTimeArray.length != noOfDaysInAWeek &&
                endTimeArray.length != noOfDaysInAWeek
            ) {
                // if all time in a week not defined, it should not apply blocked
                return false;
            }
            const todayStartTime = ParseTimeString(ConvertTime12to24(startTimeArray[dayIndex])); // office hours today's start time
            const todayEndTime = ParseTimeString(ConvertTime12to24(endTimeArray[dayIndex])); // office hours today's end time

            if (companyLevelSettings.officeHoursMonitoring == OfficeHoursMonitoring.Disabled) {
                // if office hours monitoring is disabled we dont need to apply block
                notAvailable = false;
            } else if (timeNow >= todayStartTime && timeNow <= todayEndTime) {
                // If current time lie between office hours we need to show invitation in case of only during office hours
                if (
                    companyLevelSettings.officeHoursMonitoring ==
                    OfficeHoursMonitoring.AfterOfficeHours
                ) {
                    notAvailable = true;
                    console.warn(BlockedWarningMessages.AfterOfficeHours);
                } else {
                    notAvailable = false;
                }
            } else if (
                companyLevelSettings.officeHoursMonitoring ==
                OfficeHoursMonitoring.DuringOfficeHours
            ) {
                notAvailable = true;
                console.warn(BlockedWarningMessages.DuringOfficeHours);
            } else {
                notAvailable = false;
            }
        }
        return notAvailable;
    }
}

export default CompanyLevelBlockValidator;
